import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { AccountDomainSetting, DomainSetting } from '../../models';
import { AccountService } from '../../services/account.service';
import { DomainSettingsService } from '../../services/domain-settings.service';
import {
  accountDomainSettingActions,
  getAccountsActions,
  getSectionsActions,
  sectionDomainActions,
} from '../actions/domain-settings-edit.actions';
import {
  getSelectedAccountDomainSetting,
  getSelectedAccountId,
  State,
} from '../reducers';

@Injectable()
export class DomainSettingsEditEffects {
  getAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAccountsActions.get),
      switchMap(({ accountQuery }) =>
        this.accountService.getAccounts(accountQuery).pipe(
          map((accounts) => getAccountsActions.success({ accounts })),
          catchError(() => {
            this.toastService.error(
              'Could not fetch accounts',
              'Request failed',
            );
            return [getAccountsActions.fail()];
          }),
        ),
      ),
    ),
  );

  setSelectedAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(accountDomainSettingActions.setAccount),
      switchMap(({ accountId }) => [
        accountDomainSettingActions.setAccountSuccess({ accountId }),
        getSectionsActions.get({ accountId }),
      ]),
    ),
  );

  getSections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSectionsActions.get),
      switchMap(({ accountId }) =>
        this.accountService.getSections(accountId).pipe(
          map((sections) => getSectionsActions.success({ sections })),
          catchError(() => {
            this.toastService.error(
              'Could not fetch sections',
              'Request failed',
            );
            return [getSectionsActions.fail()];
          }),
        ),
      ),
    ),
  );

  selectSection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sectionDomainActions.selectSectionDomain),
      switchMap((action) =>
        of(action).pipe(
          withLatestFrom(this.store.pipe(select(getSelectedAccountId))),
          switchMap(([{ sectionId }, accountId]) => [
            sectionDomainActions.selectSectionDomainSuccess({
              sectionId,
            }),
          ]),
        ),
      ),
    ),
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(accountDomainSettingActions.update),
      withLatestFrom(this.store.pipe(select(getSelectedAccountDomainSetting))),
      exhaustMap(([_, selectedAccountDomainSetting]) => {
        const accountDomainSetting = this.toAccountDomainName(
          selectedAccountDomainSetting,
        );
        return this.domainSettingService.update(accountDomainSetting).pipe(
          switchMap(() => {
            this.router.navigate(['/private-domain']);
            return [
              accountDomainSettingActions.updateSuccess(),
              accountDomainSettingActions.clear(),
            ];
          }),
          catchError((err) => {
            this.toastService.error(
              `${this.getErrorMessage(err)}`,
              'Request failed',
            );
            return [accountDomainSettingActions.updateFail()];
          }),
        );
      }),
    ),
  );

  save$ = createEffect(() =>
    this.actions$.pipe(
      ofType(accountDomainSettingActions.save),
      withLatestFrom(this.store.pipe(select(getSelectedAccountDomainSetting))),
      exhaustMap(([_, selectedAccountDomainSetting]) => {
        const accountDomainSetting = this.toAccountDomainName(
          selectedAccountDomainSetting,
        );
        return this.domainSettingService.save(accountDomainSetting).pipe(
          switchMap(() => {
            this.router.navigate(['/private-domain']);
            return [
              accountDomainSettingActions.saveSuccess(),
              accountDomainSettingActions.clear(),
            ];
          }),
          catchError((err) => {
            this.toastService.error(
              `${this.getErrorMessage(err)}`,
              'Request failed',
            );
            return [accountDomainSettingActions.saveFail()];
          }),
        );
      }),
    ),
  );

  private getErrorMessage(err: HttpErrorResponse) {
    const { detail } = (err && err.error) || {};
    return detail || 'Something went wrong';
  }

  private toAccountDomainName(accountDomainSetting: AccountDomainSetting) {
    const domainSetting: DomainSetting = {} as DomainSetting;
    domainSetting.accountId = accountDomainSetting.accountId;
    domainSetting.enabled = accountDomainSetting.enabled;
    if (accountDomainSetting.accountId) {
      domainSetting.domainNames = {
        account: accountDomainSetting.domainNames.accountDomain,
      };

      if (accountDomainSetting.domainNames.sections) {
        domainSetting.domainNames.sections = {};
        accountDomainSetting.domainNames.sections.forEach((section) => {
          const { id, domainName } = section;
          domainSetting.domainNames.sections[id] = { domainName };
        });
      }
    }
    return domainSetting;
  }

  constructor(
    private readonly actions$: Actions,
    private readonly accountService: AccountService,
    private readonly domainSettingService: DomainSettingsService,
    private readonly router: Router,
    private readonly toastService: ToastrService,
    private readonly store: Store<State>,
  ) {}
}
